/**
 * The four custom validators the original wrote for `validate.js`, ported as predicates.
 *
 * They are separate from the schema because each one carries a rule that is easy to get subtly wrong and
 * `validate.test.js` pins all four — the tests here are that file, case for case.
 */

/** apps-api's JSON field ceiling: 256 kB, measured on the **stringified** value. */
export const JSON_FIELD_MAX_LENGTH = 256 * 1024;
/** A deliberately smaller ceiling for the fields that never need the full 256 kB. */
export const SMALL_JSON_FIELD_MAX_LENGTH = 4 * 1024;
/** MySQL `TEXT`, which is what backs the two markdown columns. */
export const TEXT_FIELD_MAX_LENGTH = 64 * 1024;

/**
 * Whether a stringified value fits a byte ceiling.
 *
 * Measured on `JSON.stringify` of the **parsed** value, not on the text the user typed: the original
 * does the same, so whitespace and indentation in the editor do not count against the limit. Two forms
 * of the same object therefore always agree.
 */
export const fitsStringified = (value: unknown, maximumLength: number): boolean =>
  JSON.stringify(value ?? null).length <= maximumLength;

/**
 * A repository image name must not be a URL.
 *
 * apps-api wants `[registry/]vendor/component`, and a pasted `https://…` is the mistake this catches. The
 * original tests both schemes explicitly rather than a generic URL match, so a value like
 * `123456.dkr.ecr.us-east-1.amazonaws.com/keboola/x` — which *is* URL-shaped — stays valid.
 */
export const isNotUrlLike = (value: string): boolean => !/^https?:\/\//.test(value);

/**
 * A Docker memory limit: a positive integer with a `b` / `k` / `m` / `g` suffix.
 *
 * The suffix is required — a bare `128` is rejected, as is any negative value, since the pattern is
 * anchored and does not admit a sign.
 */
export const isMemoryLimit = (value: string): boolean => /^\d+[bkmg]$/.test(value);

export type JsonTextResult =
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'parsed'; value: unknown };

/**
 * Parses JSON editor text.
 *
 * Empty (or whitespace-only) text is `empty`, not `invalid`: the original normalises a cleared editor to
 * `null` on blur, and `null` passes its `object` validator. So clearing a JSON field is how you remove
 * its value, and that must not read as a validation error.
 */
export const parseJsonText = (text: string): JsonTextResult => {
  if (text.trim() === '') return { kind: 'empty' };
  try {
    return { kind: 'parsed', value: JSON.parse(text) as unknown };
  } catch {
    return { kind: 'invalid' };
  }
};

/** A JSON object — not an array, and not a bare scalar. Matches the original's `object` validator. */
export const isJsonObject = (value: unknown): boolean =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
